import { useCallback, useEffect, useRef, useState } from 'react'
import { RecordedVoice } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/types'
import { Recorder } from './recorder'
import { VoicePreview } from './voice-preview'
import { RecorderStatus } from '../../send-field.type'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { showSendVoiceDialog } from '$comp/modals/send-voice'
import { useHistoryCustomBack } from 'weblite-router'
import { showDiscardRecordingVoiceConfirmModal } from '$comp/modals/confirmation-dialog'

interface Props {
  recorderState: RecorderStatus
  sendVoice: (voice: RecordedVoice, caption?: string) => unknown
  setRecorderState: (state: RecorderStatus) => unknown
  roomId: string
}
export function VoiceRecorder({
  recorderState,
  sendVoice,
  setRecorderState,
  roomId,
}: Props) {
  const [recordedVoice, setRecordedVoice] = useState<RecordedVoice>()
  const isDesktop = useIsDesktopSize()

  const isSendVoiceDialogOpenRef = useRef(false)

  const shouldOpenSendDialog =
    recorderState === 'PREVIEW' && recordedVoice && isDesktop

  const recording =
    recorderState === 'STARTING' ||
    recorderState === 'RECORD' ||
    recorderState === 'WAITING'

  useHistoryCustomBack(
    () => {
      showDiscardRecordingVoiceConfirmModal(recording)
    },
    [recording],
    recorderState !== 'IDLE',
  )

  const sendRecordedVoice = useCallback(() => {
    if (!recordedVoice) return
    sendVoice(recordedVoice)
  }, [sendVoice, recordedVoice])

  useEffect(() => {
    if (recorderState === 'IDLE') setRecordedVoice(undefined)
  }, [recorderState])

  useEffect(() => {
    setRecordedVoice(undefined)
  }, [roomId])

  useEffect(() => {
    if (isSendVoiceDialogOpenRef.current) return

    if (shouldOpenSendDialog) {
      isSendVoiceDialogOpenRef.current = true
      showSendVoiceDialog({
        recordedVoice,
        onConfirm: (caption?: string) => {
          isSendVoiceDialogOpenRef.current = false
          sendVoice(recordedVoice, caption)
        },
        onCancel: () => {
          isSendVoiceDialogOpenRef.current = false
          setRecorderState('IDLE')
        },
      })
    }
  }, [recordedVoice, sendVoice, setRecorderState, shouldOpenSendDialog])

  return (
    <>
      {recording && (
        <Recorder
          state={recorderState}
          setRecorderState={setRecorderState}
          setRecorderVoice={setRecordedVoice}
        />
      )}
      {recorderState === 'PREVIEW' && recordedVoice && !isDesktop && (
        <VoicePreview
          recorderVoice={recordedVoice}
          onReset={() => setRecorderState('IDLE')}
          onSend={sendRecordedVoice}
        />
      )}
    </>
  )
}
